import { t } from "@/i18n";

/* Labels for the codes the engine speaks — diets, interest domains, roles,
   sides, generations. The codes are stable and English; what a person reads
   comes from the dictionary, so a language switch re-labels every chip without
   the engine noticing. */

export const DIET_TAGS = [
  "vegetarian",
  "vegan",
  "pescatarian",
  "gluten_free",
  "lactose_free",
  "nut_allergy",
  "shellfish_allergy",
  "halal",
  "kosher",
  "no_pork",
  "no_alcohol",
  "low_salt",
  "kids_menu",
  "other",
];

export const ALLERGY_TAGS = new Set(["nut_allergy", "shellfish_allergy"]);

export const DOMAIN_IDS = [
  "outdoors_adventure",
  "endurance_sport",
  "team_sport",
  "motors_cars",
  "water_sea",
  "performing_arts",
  "music_making",
  "music_listening",
  "visual_arts",
  "books_writing",
  "film_series",
  "food_cooking",
  "wine_spirits",
  "travel",
  "tech_startups",
  "science_research",
  "medicine_care",
  "teaching_kids",
  "business_finance",
  "crafts_making",
  "garden_nature",
  "animals_pets",
  "games_puzzles",
  "wellness_yoga",
  "community_faith",
  "family_local",
];

const ROLE_IDS = ["couple", "parent", "witness"];
const SIDE_IDS = ["bride", "groom", "both"];
const AGE_IDS = ["child", "young_adult", "adult", "middle_age", "senior"];

/** Getter-backed maps: `SIDE_LABELS.bride` reads the current language each
    time it is touched, so Object.entries(SIDE_LABELS) lists live labels. */
const liveMap = (ids, prefix) => {
  const map = {};
  ids.forEach((id) =>
    Object.defineProperty(map, id, {
      enumerable: true,
      get: () => t(`${prefix}.${id}`, id),
    })
  );
  return map;
};

export const ROLE_LABELS = liveMap(ROLE_IDS, "role");
export const SIDE_LABELS = liveMap(SIDE_IDS, "side");
export const AGE_LABELS = liveMap(AGE_IDS, "age");
export const DIET_LABELS = liveMap(DIET_TAGS, "diet");
export const DIET_SHORT = liveMap(DIET_TAGS, "dietShort");
export const DOMAIN_LABELS = liveMap(DOMAIN_IDS, "domain");

export const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");

export const dietLabel = (tag) => t(`diet.${tag}`, tag);
export const dietShort = (tag) => t(`dietShort.${tag}`, tag);
export const domainLabel = (id) => t(`domain.${id}`, id.replace(/_/g, " "));

export const guestMapOf = (plan) => {
  const map = {};
  (plan?.guests || []).forEach((guest) => {
    map[guest.id] = guest;
  });
  return map;
};

/** guestId -> array of guest ids they must not sit with */
export const avoidMapOf = (plan) => {
  const map = {};
  (plan?.constraints?.avoid_pairs || []).forEach(([a, b]) => {
    map[a] = [...(map[a] || []), b];
    map[b] = [...(map[b] || []), a];
  });
  return map;
};

export const wishMapOf = (plan) => {
  const map = {};
  (plan?.constraints?.wish_pairs || []).forEach(([a, b]) => {
    map[a] = [...(map[a] || []), b];
    map[b] = [...(map[b] || []), a];
  });
  return map;
};

/** Set of guest ids currently sitting with somebody they asked to avoid. */
export const conflictedGuestIds = (plan) => {
  const flagged = new Set();
  (plan?.validation?.tables || []).forEach((table) => {
    (table.conflicts || []).forEach(([a, b]) => {
      flagged.add(a);
      flagged.add(b);
    });
  });
  return flagged;
};

export const unseatedGuestIds = (plan) => {
  const ids = [];
  (plan?.unseated || []).forEach((item) => ids.push(...item.guest_ids));
  return ids;
};

export const percent = (value) => `${Math.round(value || 0)}%`;
